import { useState } from "react";

import { BulkActionsApiContext } from "components/BulkActions/Context";
import { type BulkActionItemResults } from "components/BulkActions/helpers";
import { BulkActionResultTabs, BulkActionsResult } from "components/BulkActions/types";
import { ChevronDown } from "components/icons/generated";
import Button from "ds/components/Button";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useAnalytics, { AnalyticsPage } from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";

import { BulkActionResultsStepContinueWith } from "../types";
import { checkHasOnlyCompletedResults, checkHasOnlyFailedResults } from "./helpers";

type BulkActionsDrawerResultsStepFooterContinueWithProps = {
  bulkActionItemResults: BulkActionItemResults;
  analyticsPage: AnalyticsPage;
  rerunWithFailedOnly?: boolean;
};
export const BulkActionsDrawerResultsStepFooterContinueWith = ({
  bulkActionItemResults,
  analyticsPage,
  rerunWithFailedOnly,
}: BulkActionsDrawerResultsStepFooterContinueWithProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const { onContinueWith } = useTypedContext(BulkActionsApiContext);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
  });

  const handleContinueWith = (
    continueWith: BulkActionResultsStepContinueWith,
    method: "dropdownItem" | "button"
  ) => {
    trackSegmentAnalyticsEvent(`Bulk Actions - Drawer - Continue with ${continueWith} clicked`, {
      completedCount: bulkActionItemResults[BulkActionsResult.Completed].size,
      failedCount: bulkActionItemResults[BulkActionsResult.Failed].size,
      skippedCount: bulkActionItemResults[BulkActionsResult.Skipped].size,
      stoppedCount: bulkActionItemResults[BulkActionsResult.Stopped].size,
      method,
    });
    onContinueWith(continueWith);
  };

  const createHandleContinueWithButton =
    (continueWith: BulkActionResultsStepContinueWith) => () => {
      handleContinueWith(continueWith, "button");
    };

  const createHandleContinueWithDropdownItem =
    (continueWith: BulkActionResultsStepContinueWith) => () => {
      handleContinueWith(continueWith, "dropdownItem");
    };

  const hasOnlyCompletedResults = checkHasOnlyCompletedResults(bulkActionItemResults);
  const hasOnlyFailedResults = checkHasOnlyFailedResults(bulkActionItemResults);

  return (
    <>
      {!rerunWithFailedOnly && hasOnlyCompletedResults && (
        <Button
          size="medium"
          variant="secondary"
          onPress={createHandleContinueWithButton(BulkActionResultTabs.Completed)}
        >
          New action
        </Button>
      )}
      {hasOnlyFailedResults && (
        <Button
          size="medium"
          variant="secondary"
          onPress={createHandleContinueWithButton(BulkActionResultTabs.Failed)}
        >
          New action
        </Button>
      )}
      {!rerunWithFailedOnly && !hasOnlyCompletedResults && !hasOnlyFailedResults && (
        <DropdownMenu
          onOpenChange={setDropdownOpen}
          isOpen={isDropdownOpen}
          triggerComponent={
            <Button
              onPress={() => {
                trackSegmentAnalyticsEvent("Bulk actions - Drawer - New action dropdown opened");
              }}
              size="medium"
              variant="secondary"
              endIcon={ChevronDown}
            >
              New action
            </Button>
          }
        >
          {!rerunWithFailedOnly && (
            <DropdownMenuItem onAction={createHandleContinueWithDropdownItem("all")}>
              With all
            </DropdownMenuItem>
          )}
          {!rerunWithFailedOnly && (
            <DropdownMenuItem
              onAction={createHandleContinueWithDropdownItem(BulkActionResultTabs.Completed)}
            >
              With completed
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onAction={createHandleContinueWithDropdownItem(BulkActionResultTabs.Failed)}
          >
            With failed
          </DropdownMenuItem>
        </DropdownMenu>
      )}
    </>
  );
};
